import { useState, useEffect } from "react";
import { rpcCall } from "../ws/client";
import { PaymentDetail, PaymentMethod } from "../ws/types";
import "../styles/PaymentDetails.scss";

interface PaymentDetailsProps {
    wallet: string;
    onDetailsAdded?: () => void;
}

export function PaymentDetails({ wallet, onDetailsAdded }: PaymentDetailsProps) {
    const [details, setDetails] = useState<PaymentDetail[]>([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        payment_method: "Sber" as PaymentMethod,
        account_number: "",
        account_name: "",
        bank_name: "",
        additional_info: ""
    });

    useEffect(() => {
        loadPaymentDetails();
    }, [wallet]);

    const loadPaymentDetails = async () => {
        setLoading(true);
        try {
            const response = await rpcCall<
                { wallet: string; active_only?: boolean },
                { payment_details: PaymentDetail[] }
            >("payment_details.list", {
                wallet,
                active_only: true
            });

            if (response.result?.payment_details) {
                setDetails(response.result.payment_details);
            }
        } catch (error) {
            console.error("Failed to load payment details:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            await rpcCall("payment_details.create", {
                wallet,
                ...formData
            });

            setFormData({
                payment_method: "Sber",
                account_number: "",
                account_name: "",
                bank_name: "",
                additional_info: ""
            });
            setShowForm(false);
            await loadPaymentDetails();
            
            // Call callback if provided
            if (onDetailsAdded) {
                onDetailsAdded();
            }
        } catch (error) {
            console.error("Failed to create payment detail:", error);
            alert("Не удалось создать платежные реквизиты");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (detailId: string) => {
        if (!confirm("Вы уверены, что хотите удалить эти реквизиты?")) {
            return;
        }

        try {
            await rpcCall("payment_details.delete", {
                detail_id: detailId,
                wallet
            });
            await loadPaymentDetails();
        } catch (error) {
            console.error("Failed to delete payment detail:", error);
            alert("Не удалось удалить реквизиты");
        }
    };

    const paymentMethodLabels: Record<PaymentMethod, string> = {
        Sber: "Сбербанк",
        Tinkoff: "Тинькофф",
        Raiffeisen: "Райффайзен",
        Alpha: "Альфа-Банк",
        Ozon: "Озон Банк",
        SBP: "СБП (Система быстрых платежей)"
    };

    return (
        <div className="payment-details">
            <h2>Платежные реквизиты</h2>
            <br/>
            <div className="payment-details__header">
                <button
                    className="btn btn-primary"
                    onClick={() => setShowForm(!showForm)}
                    disabled={loading}
                >
                    {showForm ? "Отмена" : "Добавить реквизиты"}
                </button>
            </div>

            {showForm && (
                <form className="payment-details__form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Платежная система</label>
                        <select
                            value={formData.payment_method}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    payment_method: e.target.value as PaymentMethod
                                })
                            }
                            required
                        >
                            {Object.entries(paymentMethodLabels).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Номер счета/карты</label>
                        <input
                            type="text"
                            value={formData.account_number}
                            onChange={(e) =>
                                setFormData({ ...formData, account_number: e.target.value })
                            }
                            placeholder="1234 5678 9012 3456"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>ФИО владельца</label>
                        <input
                            type="text"
                            value={formData.account_name}
                            onChange={(e) =>
                                setFormData({ ...formData, account_name: e.target.value })
                            }
                            placeholder="Иванов Иван Иванович"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Название банка (необязательно)</label>
                        <input
                            type="text"
                            value={formData.bank_name}
                            onChange={(e) =>
                                setFormData({ ...formData, bank_name: e.target.value })
                            }
                            placeholder="ПАО Сбербанк"
                        />
                    </div>

                    <div className="form-group">
                        <label>Дополнительная информация (необязательно)</label>
                        <textarea
                            value={formData.additional_info}
                            onChange={(e) =>
                                setFormData({ ...formData, additional_info: e.target.value })
                            }
                            placeholder="Дополнительные детали"
                            rows={3}
                        />
                    </div>

                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Сохранение..." : "Сохранить"}
                    </button>
                </form>
            )}

            <div className="payment-details__list">
                {loading && details.length === 0 ? (
                    <div className="loading">Загрузка...</div>
                ) : details.length === 0 ? (
                    <div className="empty-state">
                        <p>У вас пока нет сохраненных платежных реквизитов</p>
                        <p className="hint">
                            Добавьте реквизиты, чтобы использовать их при создании ордеров
                        </p>
                    </div>
                ) : (
                    details.map((detail) => (
                        <div key={detail.id} className="payment-detail-card">
                            <div className="payment-detail-card__header">
                                <span className="payment-method">
                                    {paymentMethodLabels[detail.payment_method]}
                                </span>
                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDelete(detail.id)}
                                    disabled={loading}
                                >
                                    Удалить
                                </button>
                            </div>
                            <div className="payment-detail-card__body">
                                <div className="detail-row">
                                    <span className="label">Номер:</span>
                                    <span className="value">{detail.account_number}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Владелец:</span>
                                    <span className="value">{detail.account_name}</span>
                                </div>
                                {detail.bank_name && (
                                    <div className="detail-row">
                                        <span className="label">Банк:</span>
                                        <span className="value">{detail.bank_name}</span>
                                    </div>
                                )}
                                {detail.additional_info && (
                                    <div className="detail-row">
                                        <span className="label">Дополнительно:</span>
                                        <span className="value">{detail.additional_info}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
